import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, FileText, CheckCircle2, XCircle, Receipt, ClipboardList, ShieldCheck } from "lucide-react";

const FUNCTIONS = ["power", "display", "touch", "charging", "front_camera", "rear_camera",
  "speakers", "microphone", "wifi", "cellular", "bluetooth", "biometrics", "buttons"];
const RESULTS = ["Pass", "Fail", "Unable to Test", "N/A"];
const CONDITION_ITEMS = ["Screen cracked", "Back glass cracked", "Frame damage", "Camera damage",
  "Scratches/scuffs", "Liquid exposure suspected", "Device does not power on", "Other"];

export default function AdminBookingDetail() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try { const { data } = await api.get(`/admin/bookings/${bookingId}`); setData(data); }
    catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
      else toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);
  if (!data) return <div className="pt-32 text-center">Loading…</div>;

  const b = data.booking;
  const latest = data.estimates?.[data.estimates.length - 1];
  return (
    <div className="pt-24 pb-16 max-w-6xl mx-auto px-4">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" /> Back to admin</Link>
      <div className="flex flex-wrap items-baseline justify-between mb-6 gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-mono">{b.ticket_code}</h1>
          <div className="text-slate-600 mt-1">{b.brand} {b.model_name} · {b.customer.name} · <span className="text-amber-700 uppercase tracking-widest text-xs font-semibold">{b.status.replace(/_/g, " ")}</span></div>
        </div>
        {b.claim_number && <div className="text-sm text-slate-500">Claim <span className="font-mono font-bold text-slate-900">{b.claim_number}</span></div>}
      </div>
      <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
        {["overview", "check-in", "estimate", "supplemental", "qc", "invoice", "audit"].map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 transition ${tab === t ? "border-amber-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.replace("-", " ")}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview b={b} />}
      {tab === "check-in" && <CheckInTab b={b} claim={data.claim_check} reload={load} />}
      {tab === "estimate" && <EstimateTab b={b} estimates={data.estimates} latest={latest} reload={load} />}
      {tab === "supplemental" && <SupplementalTab b={b} supps={data.supplementals} reload={load} />}
      {tab === "qc" && <QCTab b={b} qcs={data.qc_records} reload={load} />}
      {tab === "invoice" && <InvoiceTab b={b} invoice={data.invoice} reload={load} />}
      {tab === "audit" && <AuditTab audits={data.audit} />}
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="p-5 rounded-2xl border border-slate-200 bg-white mb-4">
      {title && <div className="flex items-center gap-2 mb-3 text-sm font-bold text-slate-900">{Icon && <Icon className="w-4 h-4" />} {title}</div>}
      {children}
    </div>
  );
}

function Overview({ b }) {
  return (
    <Card title="Customer & device" icon={ClipboardList}>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div><div className="text-slate-500 text-xs">Customer</div><div>{b.customer.name}</div><div>{b.customer.email}</div><div>{b.customer.phone}</div></div>
        <div><div className="text-slate-500 text-xs">Address</div><div>{b.customer.street || "—"}</div><div>{b.customer.city} {b.customer.state} {b.customer.zip}</div></div>
        <div><div className="text-slate-500 text-xs">Device</div><div>{b.brand} {b.model_name} ({b.device_type})</div></div>
        <div><div className="text-slate-500 text-xs">Drop-off</div><div>{b.dropoff_date} {b.dropoff_time}</div></div>
        <div className="sm:col-span-2"><div className="text-slate-500 text-xs">Services</div><div>{(b.services || []).join(", ") || "—"}</div></div>
        {b.customer.notes && <div className="sm:col-span-2"><div className="text-slate-500 text-xs">Notes</div><div>{b.customer.notes}</div></div>}
      </div>
    </Card>
  );
}

function CheckInTab({ b, claim, reload }) {
  const [form, setForm] = useState({
    device: { serial: "", imei: "", color: "", accessories: "", case_present: false, sim_present: false },
    pre_repair_condition: [], function_test: {}, technician_notes: "",
    intake_photo_ids: [], data_access_ack: false, backup_ack: false,
    replaced_parts_preference: "",
  });
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const setFT = (k, v) => setForm((f) => ({ ...f, function_test: { ...f.function_test, [k]: v } }));
  const toggleCond = (c) => setForm((f) => ({ ...f, pre_repair_condition: f.pre_repair_condition.includes(c) ? f.pre_repair_condition.filter((x) => x !== c) : [...f.pre_repair_condition, c] }));
  const submit = async () => {
    if (!form.data_access_ack || !form.backup_ack) { toast.error("Both acknowledgments required"); return; }
    if (form.replaced_parts_preference !== "return" && form.replaced_parts_preference !== "waive") { toast.error("Please record the customer's replaced-parts preference"); return; }
    setSaving(true);
    try { await api.post(`/admin/bookings/${b.id}/check-in`, form); toast.success(`Claim check created`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Check-in failed"); }
    finally { setSaving(false); }
  };
  if (claim) return (
    <Card title={`Claim check ${claim.claim_number}`} icon={ClipboardCheck}>
      <div className="flex justify-end gap-2 mb-3">
        <button data-testid="email-claim-btn" disabled={sendingEmail} onClick={async () => {
          if (sendingEmail) return;
          setSendingEmail(true);
          try { const { data } = await api.post(`/admin/bookings/${b.id}/email-claim-check`); toast.success(`Emailed to ${data.to}`); }
          catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
          finally { setSendingEmail(false); }
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed">{sendingEmail ? "Sending…" : "Email to customer"}</button>
        <Link to={`/admin/bookings/${b.id}/claim-check`} data-testid="print-claim-link" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
          Print / PDF
        </Link>
      </div>
      <p className="text-sm text-slate-600 mb-2">Device checked in by <strong>{claim.technician}</strong> on {new Date(claim.created_at).toLocaleString()}.</p>
      {claim.ca_notice_version ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2 whitespace-pre-wrap">{claim.ca_notice_version}</div>
      ) : (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
          No California statutory notice text has been configured yet. Set it in Admin → Business Settings before printing customer copies.
        </div>
      )}
    </Card>
  );
  return (
    <>
      <Card title="Device details" icon={ClipboardList}>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {["serial", "imei", "color", "accessories"].map((k) => (
            <label key={k} className="block"><span className="text-xs text-slate-500 uppercase">{k}</span>
              <input data-testid={`checkin-${k}`} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={form.device[k]}
                onChange={(e) => setForm((f) => ({ ...f, device: { ...f.device, [k]: e.target.value } }))} /></label>
          ))}
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="checkin-case-present" checked={form.device.case_present} onChange={(e) => setForm((f) => ({ ...f, device: { ...f.device, case_present: e.target.checked } }))} /> Case present</label>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="checkin-sim-present" checked={form.device.sim_present} onChange={(e) => setForm((f) => ({ ...f, device: { ...f.device, sim_present: e.target.checked } }))} /> SIM present</label>
        </div>
      </Card>
      <Card title="Pre-repair condition">
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          {CONDITION_ITEMS.map((c) => (
            <label key={c} className="flex items-center gap-2">
              <input type="checkbox" checked={form.pre_repair_condition.includes(c)} onChange={() => toggleCond(c)} data-testid={`cond-${c.replace(/\s+/g, "-")}`} />
              {c}
            </label>
          ))}
        </div>
      </Card>
      <Card title="Function test">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {FUNCTIONS.map((fn) => (
            <label key={fn} className="block">
              <span className="text-xs text-slate-500 uppercase">{fn.replace("_", " ")}</span>
              <select data-testid={`ft-${fn}`} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5"
                value={form.function_test[fn] || ""} onChange={(e) => setFT(fn, e.target.value)}>
                <option value="">—</option>
                {RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
          ))}
        </div>
      </Card>
      <Card title="Required acknowledgments">
        <label className="flex items-start gap-2 text-sm mb-2"><input type="checkbox" data-testid="ack-backup" checked={form.backup_ack} onChange={(e) => setForm((f) => ({ ...f, backup_ack: e.target.checked }))} className="mt-1" /> Customer understands that repair can involve a risk of data loss and has backed up important data or accepts the risk of proceeding without a backup.</label>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" data-testid="ack-access" checked={form.data_access_ack} onChange={(e) => setForm((f) => ({ ...f, data_access_ack: e.target.checked }))} className="mt-1" /> Customer authorizes The Repair Nook to access and test device functions only to the extent reasonably necessary to diagnose, perform, and verify the repair services authorized.</label>
      </Card>
      <Card title="Replaced-parts preference">
        <p className="text-xs text-slate-500 mb-2">California customers may elect to receive their replaced parts back where legally applicable. Record the customer's preference at intake.</p>
        <label className="flex items-start gap-2 text-sm mb-2">
          <input type="radio" name="rpp" data-testid="rpp-return" checked={form.replaced_parts_preference === "return"} onChange={() => setForm((f) => ({ ...f, replaced_parts_preference: "return" }))} className="mt-1" />
          Return replaced parts to me when applicable.
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="radio" name="rpp" data-testid="rpp-waive" checked={form.replaced_parts_preference === "waive"} onChange={() => setForm((f) => ({ ...f, replaced_parts_preference: "waive" }))} className="mt-1" />
          I waive return of replaced parts to the extent permitted by applicable law.
        </label>
      </Card>
      <Card>
        <label className="block text-sm mb-3">
          <span className="text-xs text-slate-500 uppercase">Technician notes</span>
          <textarea data-testid="tech-notes" rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={form.technician_notes} onChange={(e) => setForm((f) => ({ ...f, technician_notes: e.target.value }))} />
        </label>
        <button data-testid="checkin-submit" onClick={submit} disabled={saving} className="px-5 py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40">
          {saving ? "Checking in…" : "Check in device & generate claim check"}
        </button>
      </Card>
    </>
  );
}

function EstimateTab({ b, estimates, latest, reload }) {
  const [form, setForm] = useState({ diagnosis: "", lines: [{ label: "", part_quality: "AFTERMARKET_STANDARD", parts_cost: 0, labor_cost: 0 }], tax_rate_percent: 7.25, notes: "" });
  const [saving, setSaving] = useState(false);
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { label: "", part_quality: "AFTERMARKET_STANDARD", parts_cost: 0, labor_cost: 0 }] }));
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/admin/bookings/${b.id}/final-estimate`, form); toast.success("Estimate sent to customer for authorization"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <>
      {estimates?.map((e) => (
        <Card key={e.id} title={`Estimate v${e.version} — ${e.status.replace(/_/g, " ")}`} icon={FileText}>
          <p className="text-sm mb-2"><strong>Diagnosis:</strong> {e.diagnosis}</p>
          <div className="text-xs text-slate-600 mb-2">Total ${e.totals.grand_total.toFixed(2)} (parts ${e.totals.parts_total.toFixed(2)} · labor ${e.totals.labor_total.toFixed(2)} · tax ${e.totals.tax_total.toFixed(2)})</div>
          {e.customer_signature && <div className="text-xs text-emerald-700">Signed by {e.customer_signature} on {new Date(e.customer_decision_at).toLocaleString()}</div>}
        </Card>
      ))}
      <Card title={latest ? "Create revised estimate" : "Create final written estimate"} icon={FileText}>
        <label className="block text-sm mb-2"><span className="text-xs text-slate-500 uppercase">Diagnosis</span><textarea data-testid="est-diagnosis" rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={form.diagnosis} onChange={(e) => setForm((f) => ({ ...f, diagnosis: e.target.value }))} /></label>
        {form.lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2 text-sm">
            <input placeholder="Line item" data-testid={`est-line-label-${i}`} className="col-span-4 rounded-lg border border-slate-200 px-2 py-1.5" value={l.label} onChange={(e) => { const lines = [...form.lines]; lines[i].label = e.target.value; setForm({ ...form, lines }); }} />
            <select className="col-span-3 rounded-lg border border-slate-200 px-2 py-1.5" value={l.part_quality} onChange={(e) => { const lines = [...form.lines]; lines[i].part_quality = e.target.value; setForm({ ...form, lines }); }}>
              <option value="AFTERMARKET_STANDARD">Aftermarket Standard</option>
              <option value="AFTERMARKET_PREMIUM">Aftermarket Premium</option>
              <option value="REFURBISHED_ORIGINAL">Refurbished Original</option>
              <option value="GENUINE_MANUFACTURER">Genuine Manufacturer</option>
            </select>
            <input type="number" step="0.01" placeholder="Parts" data-testid={`est-line-parts-${i}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5" value={l.parts_cost} onChange={(e) => { const lines = [...form.lines]; lines[i].parts_cost = parseFloat(e.target.value) || 0; setForm({ ...form, lines }); }} />
            <input type="number" step="0.01" placeholder="Labor" data-testid={`est-line-labor-${i}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5" value={l.labor_cost} onChange={(e) => { const lines = [...form.lines]; lines[i].labor_cost = parseFloat(e.target.value) || 0; setForm({ ...form, lines }); }} />
          </div>
        ))}
        <button type="button" onClick={addLine} className="text-xs text-slate-500 hover:text-slate-900">+ Add line</button>
        <label className="block text-sm mt-3"><span className="text-xs text-slate-500 uppercase">Sales tax %</span><input type="number" step="0.01" data-testid="est-tax" className="mt-1 w-32 rounded-lg border border-slate-200 px-2 py-1.5" value={form.tax_rate_percent} onChange={(e) => setForm((f) => ({ ...f, tax_rate_percent: parseFloat(e.target.value) || 0 }))} /></label>
        <button data-testid="est-submit" onClick={submit} disabled={saving} className="mt-4 px-5 py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40">
          {saving ? "Sending…" : "Send to customer for authorization"}
        </button>
      </Card>
    </>
  );
}

function SupplementalTab({ b, supps, reload }) {
  const [form, setForm] = useState({ reason: "", additional_lines: [{ label: "", parts_cost: 0, labor_cost: 0 }], tax_rate_percent: 7.25 });
  const submit = async () => {
    try { await api.post(`/admin/bookings/${b.id}/supplemental-authorization`, form); toast.success("Supplemental estimate created"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      {supps?.map((s) => (
        <Card key={s.id} title={`Supplemental — ${s.status.replace(/_/g, " ")}`}>
          <p className="text-sm">{s.reason}</p>
          <div className="text-xs text-slate-600 mt-1">Additional ${s.additional_totals.grand_total.toFixed(2)} · new total ${s.new_grand_total.toFixed(2)}</div>
        </Card>
      ))}
      <Card title="Request supplemental authorization">
        <label className="block text-sm mb-2"><span className="text-xs text-slate-500 uppercase">Reason</span><textarea rows={2} data-testid="supp-reason" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} /></label>
        <div className="grid grid-cols-12 gap-2 mb-2 text-sm">
          <input placeholder="Line item" data-testid="supp-label" className="col-span-6 rounded-lg border border-slate-200 px-2 py-1.5" value={form.additional_lines[0].label} onChange={(e) => setForm((f) => ({ ...f, additional_lines: [{ ...f.additional_lines[0], label: e.target.value }] }))} />
          <input type="number" step="0.01" placeholder="Parts" data-testid="supp-parts" className="col-span-3 rounded-lg border border-slate-200 px-2 py-1.5" value={form.additional_lines[0].parts_cost} onChange={(e) => setForm((f) => ({ ...f, additional_lines: [{ ...f.additional_lines[0], parts_cost: parseFloat(e.target.value) || 0 }] }))} />
          <input type="number" step="0.01" placeholder="Labor" data-testid="supp-labor" className="col-span-3 rounded-lg border border-slate-200 px-2 py-1.5" value={form.additional_lines[0].labor_cost} onChange={(e) => setForm((f) => ({ ...f, additional_lines: [{ ...f.additional_lines[0], labor_cost: parseFloat(e.target.value) || 0 }] }))} />
        </div>
        <button data-testid="supp-submit" onClick={submit} className="mt-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800">Send supplemental to customer</button>
      </Card>
    </>
  );
}

function QCTab({ b, qcs, reload }) {
  const [form, setForm] = useState({ function_test: {}, repaired_function_tested: false, technician_notes: "" });
  const submit = async () => {
    try { await api.post(`/admin/bookings/${b.id}/qc`, form); toast.success("QC recorded"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setFT = (k, v) => setForm((f) => ({ ...f, function_test: { ...f.function_test, [k]: v } }));
  return (
    <>
      {qcs?.map((q) => (
        <Card key={q.id} title={`QC by ${q.technician}`} icon={ShieldCheck}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {Object.entries(q.function_test).map(([k, v]) => <div key={k}><span className="text-slate-500">{k}:</span> <span className={v === "Pass" ? "text-emerald-700" : v === "Fail" ? "text-rose-600" : "text-slate-700"}>{v}</span></div>)}
          </div>
        </Card>
      ))}
      <Card title="New QC record">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {FUNCTIONS.map((fn) => (
            <label key={fn} className="block"><span className="text-xs text-slate-500 uppercase">{fn.replace("_", " ")}</span>
              <select data-testid={`qc-${fn}`} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5" value={form.function_test[fn] || ""} onChange={(e) => setFT(fn, e.target.value)}>
                <option value="">—</option>{RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="qc-repaired-tested" checked={form.repaired_function_tested} onChange={(e) => setForm((f) => ({ ...f, repaired_function_tested: e.target.checked }))} /> Repaired function specifically tested</label>
        <button data-testid="qc-submit" onClick={submit} className="mt-3 px-5 py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800">Record QC</button>
      </Card>
    </>
  );
}

function InvoiceTab({ b, invoice, reload }) {
  const [sendingEmail, setSendingEmail] = useState(false);
  const gen = async () => { try { await api.post(`/admin/bookings/${b.id}/invoice`); toast.success("Invoice generated"); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  if (!invoice) return <Card title="No invoice yet"><button data-testid="gen-invoice" onClick={gen} className="px-5 py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800">Generate invoice</button></Card>;
  return (
    <Card title={`Invoice ${invoice.invoice_number}`} icon={Receipt}>
      <div className="flex justify-end gap-2 mb-3">
        <button data-testid="email-invoice-btn" disabled={sendingEmail} onClick={async () => {
          if (sendingEmail) return;
          setSendingEmail(true);
          try { const { data } = await api.post(`/admin/bookings/${b.id}/email-invoice`); toast.success(`Emailed to ${data.to}`); }
          catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
          finally { setSendingEmail(false); }
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed">{sendingEmail ? "Sending…" : "Email to customer"}</button>
        <Link to={`/admin/bookings/${b.id}/invoice`} data-testid="print-invoice-link" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
          Print / PDF
        </Link>
      </div>
      <div className="text-sm space-y-1">
        <div>Parts total: <span className="font-mono">${invoice.parts_total.toFixed(2)}</span></div>
        <div>Labor total: <span className="font-mono">${invoice.labor_total.toFixed(2)}</span></div>
        <div>Tax: <span className="font-mono">${invoice.tax_total.toFixed(2)}</span></div>
        <div className="font-bold pt-2 border-t border-slate-100">Grand total: <span className="font-mono">${invoice.grand_total.toFixed(2)}</span></div>
        <div className="text-xs text-slate-500 mt-2">Parts paid online: {invoice.parts_paid_online ? "Yes" : "No"}</div>
      </div>
    </Card>
  );
}

function AuditTab({ audits }) {
  return (
    <Card title="Audit trail">
      <ol className="space-y-1 text-xs">
        {audits?.map((a) => (
          <li key={a.id} className="flex gap-2 border-b border-slate-100 py-1">
            <span className="text-slate-400 font-mono">{new Date(a.created_at).toLocaleString()}</span>
            <span className="font-semibold text-slate-900">{a.event}</span>
            {a.actor && <span className="text-slate-500">by {a.actor}</span>}
            {a.new_value && <span className="text-slate-500">→ {a.new_value}</span>}
          </li>
        ))}
      </ol>
    </Card>
  );
}
